import { useEffect, useState } from 'react';
import api from '../lib/api';

/**
 * Fetches the cross-app health-score quarter-over-quarter deltas
 * once and returns the entry for a single metric — one of:
 * `lei`, `pe`, `roe`, `kpi`, or `roi`.
 *
 * Returns `null` while loading or when no historical snapshot
 * exists yet. Consumers should pass the result straight into a
 * `PageHero` stat object as `{ delta, deltaText, deltaLabel,
 * deltaInverted? }` so the existing chip renderer picks it up.
 *
 * The hook is intentionally side-effect-free beyond the fetch;
 * the home page is the canonical source of NEW snapshots (it
 * POSTs `/health/snapshots` on load), so other pages only read.
 */
export function useQuarterDelta(metricKey) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/health/quarter-deltas');
        if (cancelled) return;
        const m = res?.data?.metrics?.[metricKey];
        if (!m || m.delta_pct === null || m.delta_pct === undefined) {
          setData(null);
          return;
        }
        const dp = Number(m.delta_pct);
        const sign = dp > 0 ? '+' : '';
        setData({
          delta: dp,
          deltaText: `${sign}${dp}% vs last quarter`,
          deltaLabel: 'vs last quarter',
          direction: m.direction,
          previous: m.previous,
          current: m.current,
        });
      } catch {
        if (!cancelled) setData(null);
      }
    })();
    return () => { cancelled = true; };
  }, [metricKey]);

  return data;
}

export default useQuarterDelta;
